import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Alert02Icon,
  AlertCircleIcon,
  EyeIcon,
  InformationCircleIcon,
  SlidersHorizontalIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { countries } from 'constants/countries';
import { FieldTypes } from 'constants/field-types-enum';
import { FLAG_SEVERITY_MAP, FLAG_SEVERITY_ORDER } from 'constants/forms';
import { OPERATOR_SYMBOLS, operatorTypes } from 'constants/logic';
import { useFieldArray, useWatch } from 'react-hook-form';

import { Accordion } from 'components/Accordion/Accordion';
import {
  FormMessage,
  MultipleSelect,
  Select,
  Textarea,
  ToggleSwitch,
  ValidationAlert,
} from 'components/FormComponents';

// ─── Severity config (labels/order from forms.js; UI from local) ───────────────

const SEVERITY_UI = {
  high: { icon: AlertCircleIcon, iconClass: 'text-red-2', textClass: 'text-red-2' },
  medium: { icon: Alert02Icon, iconClass: 'text-orange-3', textClass: 'text-orange-3' },
  low: { icon: InformationCircleIcon, iconClass: 'text-blue-3', textClass: 'text-blue-3' },
};

const SEVERITIES = FLAG_SEVERITY_ORDER.map(key => ({
  key,
  label: FLAG_SEVERITY_MAP[key].label,
  ...SEVERITY_UI[key],
}));

const DEFAULT_FLAG_OPERATOR = 'lower_equal_than';
const DEFAULT_BLOCK_VALUE = '1';

// ─── Memoized country options (built once, never rebuilt) ─────────────────────

const COUNTRY_OPTIONS = countries.map(c => ({ value: c.value, label: c.name }));

// ─── Options for MultiChoice / Country (for multi-select) ─────────────────────

const getMultiValueOptions = field => {
  if (field.type === FieldTypes.Country) return COUNTRY_OPTIONS;
  if (field.type === FieldTypes.MultipleChoice) {
    return (field.options?.options || []).map(o => ({ value: o.value, label: o.value }));
  }
  return [];
};

const isMultiValueField = type => type === FieldTypes.MultipleChoice || type === FieldTypes.Country;

// ─── Value selector ───────────────────────────────────────────────────────────

const RuleValueSelect = ({ name, register, value, field, selectedValues, onValuesChange }) => {
  const multiValue =
    isMultiValueField(field?.type) && Array.isArray(selectedValues) && onValuesChange;

  // Memoize per-field options so MultipleSelect doesn't see a new array on every render
  const multiOptions = useMemo(
    () => (multiValue ? getMultiValueOptions(field) : []),
    [field.type, field.options],
  );

  if (multiValue) {
    const displayValues = [...new Set(selectedValues.filter(v => v !== ''))];
    return (
      <MultipleSelect
        value={displayValues}
        onChange={onValuesChange}
        placeholder="Select values..."
        className="min-w-[160px]"
        useSearch={field.type === FieldTypes.Country}>
        {multiOptions.map(opt => (
          <MultipleSelect.Item key={opt.value} value={opt.value}>
            {opt.label}
          </MultipleSelect.Item>
        ))}
      </MultipleSelect>
    );
  }

  switch (field.type) {
    case FieldTypes.Date:
      return (
        <input
          type="date"
          {...register(name)}
          className="h-10 px-3 text-xl text-neutral-7 bg-neutral-1 border border-neutral-4 rounded-[10px] min-w-[150px]"
        />
      );
    case FieldTypes.Country:
      return (
        <Select name={name} register={register} value={value} useDefault className="min-w-[160px]">
          <Select.Item value="">Not set</Select.Item>
          {COUNTRY_OPTIONS.map(opt => (
            <Select.Item key={opt.value} value={opt.value}>
              {opt.label}
            </Select.Item>
          ))}
        </Select>
      );
    default: {
      let options = [];
      switch (field.type) {
        case FieldTypes.OpinionScale: {
          const opts = field.options || {};
          const start = opts.use_custom_steps && opts.steps_start ? parseInt(opts.steps_start) : 1;
          const end = opts.use_custom_steps && opts.steps_end ? parseInt(opts.steps_end) : 10;
          options = Array(end - start + 1)
            .fill()
            .map((_, i) => ({ value: String(start + i), label: String(start + i) }));
          break;
        }
        case FieldTypes.StarRating: {
          const end = field.options?.use_custom_stars ? parseInt(field.options.stars) : 5;
          options = Array(end)
            .fill()
            .map((_, i) => ({ value: String(i + 1), label: String(i + 1) }));
          break;
        }
        case FieldTypes.YesNo:
          options = [
            { value: 'Yes', label: 'Yes' },
            { value: 'No', label: 'No' },
          ];
          break;
        case FieldTypes.MultipleChoice:
          options = (field.options?.options || []).map(o => ({ value: o.value, label: o.value }));
          break;
        default:
          return null;
      }
      return (
        <Select name={name} register={register} value={value} useDefault className="min-w-[80px]">
          <Select.Item value="">Not set</Select.Item>
          {options.map(opt => (
            <Select.Item key={opt.value} value={opt.value}>
              {opt.label}
            </Select.Item>
          ))}
        </Select>
      );
    }
  }
};

// ─── Flag condition row ────────────────────────────────────────────────────────

const FlagConditionRow = ({
  rulesForSeverity,
  register,
  field,
  icon,
  label,
  iconClass,
  textClass,
  syncFlagValues,
  defaultOperator,
}) => {
  const first = rulesForSeverity[0];
  const ruleIndex = first?.index;
  const ruleOperator = first?.rule?.operator ?? defaultOperator;
  const multiValue = isMultiValueField(field?.type) && syncFlagValues;
  const selectedValues = multiValue
    ? rulesForSeverity.map(({ rule }) => rule.value).filter(v => v !== undefined)
    : [];
  const displayValue = first?.rule?.value ?? '';

  const availableOperators = useMemo(
    () => operatorTypes.filter(op => op.allowedTypes.includes(field.type)),
    [field.type],
  );

  const handleValuesChange = useCallback(
    newValues => {
      const normalized = Array.isArray(newValues) && newValues.length ? newValues : [''];
      syncFlagValues(first.rule.severity, normalized);
    },
    [syncFlagValues, first?.rule?.severity],
  );

  return (
    <div className="w-full min-w-0 flex flex-col gap-1">
      <div className="flex items-center gap-1 min-w-0 shrink-0 pt-3">
        <HugeiconsIcon icon={icon} size={20} className={iconClass} />
        <span className="text-neutral-6 font-medium flex items-center gap-2 text-xl whitespace-nowrap">
          <span className={textClass}>{label}</span>if value is
        </span>
      </div>
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 sm:gap-2 py-3 min-w-0 w-full">
        <div className="w-full sm:flex-1 sm:min-w-0">
          <Select
            name={`rules.${ruleIndex}.operator`}
            register={register}
            value={ruleOperator}
            useDefault
            className="w-full">
            {availableOperators.map(op => (
              <Select.Item key={op.value} value={op.value}>
                {OPERATOR_SYMBOLS[op.value] || op.label}
              </Select.Item>
            ))}
          </Select>
        </div>
        <div className="min-w-0 w-full sm:flex-1 sm:min-w-0">
          <RuleValueSelect
            name={`rules.${ruleIndex}.value`}
            register={register}
            value={displayValue}
            field={field}
            selectedValues={multiValue ? selectedValues : undefined}
            onValuesChange={multiValue ? handleValuesChange : undefined}
          />
        </div>
      </div>
    </div>
  );
};

// ─── Condition control for blocking ───────────────────────────────────────────

const ConditionControl = ({
  register,
  ruleIndex,
  blockRulesForField,
  field,
  syncBlockValues,
  defaultOperator,
}) => {
  const first = blockRulesForField?.[0];
  const ruleIdx = first?.index ?? ruleIndex;
  const ruleOperator = first?.rule?.operator ?? defaultOperator;
  const multiValue = isMultiValueField(field?.type) && syncBlockValues;
  const selectedValues = multiValue
    ? blockRulesForField.map(({ rule }) => rule.value).filter(v => v !== undefined)
    : [];
  const displayValue = first?.rule?.value ?? '';

  const availableOperators = useMemo(
    () => operatorTypes.filter(op => op.allowedTypes.includes(field.type)),
    [field.type],
  );

  const handleValuesChange = useCallback(
    newValues => {
      const normalized = Array.isArray(newValues) && newValues.length ? newValues : [''];
      syncBlockValues(normalized);
    },
    [syncBlockValues],
  );

  return (
    <div className="w-full min-w-0 flex flex-col gap-1">
      <div className="flex items-center gap-1 min-w-0 shrink-0">
        <HugeiconsIcon icon={AlertCircleIcon} size={20} className="text-red-2" />
        <span className="text-neutral-6 font-medium flex items-center gap-2 text-xl whitespace-nowrap">
          <span className="text-red-2">Block progression</span>if value is
        </span>
      </div>
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 sm:gap-2 py-3 min-w-0 w-full">
        <div className="w-full sm:flex-1 sm:min-w-0">
          <Select
            name={`rules.${ruleIdx}.operator`}
            register={register}
            value={ruleOperator}
            useDefault
            className="w-full">
            {availableOperators.map(op => (
              <Select.Item key={op.value} value={op.value}>
                {OPERATOR_SYMBOLS[op.value] || op.label}
              </Select.Item>
            ))}
          </Select>
        </div>
        <div className="min-w-0 w-full sm:flex-1 sm:min-w-0">
          <RuleValueSelect
            name={`rules.${ruleIdx}.value`}
            register={register}
            value={displayValue}
            field={field}
            selectedValues={multiValue ? selectedValues : undefined}
            onValuesChange={multiValue ? handleValuesChange : undefined}
          />
        </div>
      </div>
    </div>
  );
};

// ─── BlockRuleEditor ──────────────────────────────────────────────────────────

const BlockRuleEditor = ({
  blockIndex,
  register,
  field,
  allRules,
  fieldOrder,
  blockRulesForField,
  syncBlockValues,
  defaultOperator,
}) => (
  <div className="flex flex-col gap-2 min-w-0">
    <div className="flex flex-col gap-2 min-w-0 mt-2">
      <span className="text-neutral-6 text-xl font-medium">Message to candidate</span>
      <div className="min-h-[80px]">
        <Textarea
          register={register}
          name={`rules.${blockIndex}.message`}
          placeholder="Your rating indicates you may not be able to provide a meaningful reference. Please select a different experience."
          dynamicHeight={true}
        />
      </div>
    </div>

    {allRules[blockIndex]?.message && (
      <div className="min-w-0 w-full flex flex-col gap-2 mb-6">
        <span className="text-neutral-6 text-xl font-medium flex items-center gap-2">
          <HugeiconsIcon icon={EyeIcon} size={20} className="text-neutral-6 shrink-0" />
          Preview
        </span>
        <ValidationAlert message={allRules[blockIndex]?.message} />
        <span className="text-neutral-6 text-lg font-regular">
          This message will be shown to the candidate if the block rule is met.
        </span>
      </div>
    )}

    <div className="flex flex-col gap-3 sm:gap-4 min-w-0 w-full">
      <div className="min-w-0 w-full">
        <ConditionControl
          register={register}
          ruleIndex={blockIndex}
          blockRulesForField={blockRulesForField}
          field={field}
          syncBlockValues={isMultiValueField(field?.type) ? syncBlockValues : undefined}
          defaultOperator={defaultOperator}
        />
      </div>
    </div>
  </div>
);

// ─── RulesConfiguration ────────────────────────────────────────────────────────

export const RulesConfiguration = ({ control, register, field, rulesCount }) => {
  const allRules = useWatch({ control, name: 'rules' }) || [];
  const { append, remove, update, insert } = useFieldArray({ control, name: 'rules' });

  const [message, setMessage] = useState('');

  const fieldOrder = field.order;

  const flagRulesWithIndex = useMemo(
    () =>
      allRules
        .map((r, i) => ({ rule: r, index: i }))
        .filter(({ rule }) => rule.field === fieldOrder && rule.rule_type === 'flag'),
    [allRules, fieldOrder],
  );

  const blockRulesWithIndex = useMemo(
    () =>
      allRules
        .map((r, i) => ({ rule: r, index: i }))
        .filter(({ rule }) => rule.field === fieldOrder && rule.rule_type === 'block'),
    [allRules, fieldOrder],
  );

  const flaggingOn = flagRulesWithIndex.some(({ rule }) => !!rule.active);
  const blockRuleWithIndex = blockRulesWithIndex[0] ?? null;
  const blockingOn = blockRuleWithIndex ? !!blockRuleWithIndex.rule.active : false;

  const availableOperators = useMemo(
    () => operatorTypes.filter(op => op.allowedTypes.includes(field.type)),
    [field.type],
  );
  const defaultOperator = availableOperators[0]?.value || DEFAULT_FLAG_OPERATOR;

  const getDefaultBlockValue = useCallback(
    () =>
      field.type === FieldTypes.OpinionScale || field.type === FieldTypes.StarRating
        ? DEFAULT_BLOCK_VALUE
        : '',
    [field.type],
  );

  const prevFieldTypeRef = useRef(field?.type);
  const allRulesRef = useRef(allRules);
  allRulesRef.current = allRules;

  // Reset operator + value for all field rules when field type changes
  useLayoutEffect(() => {
    const currentType = field?.type;
    if (prevFieldTypeRef.current === currentType) return;
    prevFieldTypeRef.current = currentType;

    setMessage('We have reset the rules to the default values since the field type has changed.');

    const operators = operatorTypes.filter(op => op.allowedTypes.includes(currentType));
    const newDefaultOperator = operators[0]?.value || DEFAULT_FLAG_OPERATOR;

    const rules = allRulesRef.current;
    rules.forEach((rule, index) => {
      if (rule.field !== fieldOrder) return;
      if (rule.rule_type === 'flag' || rule.rule_type === 'block') {
        update(index, { ...rule, operator: newDefaultOperator, value: '' });
      }
    });
  }, [field?.type, fieldOrder, update]);

  // Ensure all 3 severity rows exist when flagging is on
  useLayoutEffect(() => {
    if (!flaggingOn) return;
    SEVERITIES.forEach(({ key }) => {
      const exists = flagRulesWithIndex.some(({ rule }) => rule.severity === key);
      if (!exists) {
        append({
          rule_type: 'flag',
          field: fieldOrder,
          operator: defaultOperator,
          value: '',
          severity: key,
          message: '',
          active: true,
        });
      }
    });
  }, [flaggingOn, flagRulesWithIndex.length]);

  // Sync operator across all rules in a severity group (multi-value fields only)
  useEffect(() => {
    if (!isMultiValueField(field?.type)) return;
    const rules = allRulesRef.current;
    const order = fieldOrder;

    SEVERITIES.forEach(({ key }) => {
      const group = rules
        .map((r, i) => ({ rule: r, index: i }))
        .filter(
          ({ rule }) => rule.field === order && rule.rule_type === 'flag' && rule.severity === key,
        );
      if (group.length <= 1) return;
      const firstOp = group[0].rule?.operator;
      group.slice(1).forEach(({ rule, index }) => {
        if (rule?.operator !== firstOp) update(index, { ...rule, operator: firstOp });
      });
    });

    const blockGroup = rules
      .map((r, i) => ({ rule: r, index: i }))
      .filter(({ rule }) => rule.field === order && rule.rule_type === 'block');
    if (blockGroup.length > 1) {
      const firstOp = blockGroup[0].rule?.operator;
      blockGroup.slice(1).forEach(({ rule, index }) => {
        if (rule?.operator !== firstOp) update(index, { ...rule, operator: firstOp });
      });
    }
  }, [allRules, fieldOrder, field?.type, update]);

  // Sync multi-select flag values: one rule per selected value per severity
  const syncFlagValues = useCallback(
    (severity, newValues) => {
      const rules = allRulesRef.current;
      const order = fieldOrder;
      const group = rules
        .map((r, i) => ({ rule: r, index: i }))
        .filter(
          ({ rule }) =>
            rule.field === order && rule.rule_type === 'flag' && rule.severity === severity,
        );
      const normalized = Array.isArray(newValues) && newValues.length ? newValues : [''];
      const operator = group[0]?.rule?.operator ?? defaultOperator;
      const template = group[0]?.rule
        ? { ...group[0].rule }
        : {
            rule_type: 'flag',
            field: order,
            operator,
            value: '',
            severity,
            message: '',
            active: true,
          };

      const toRemove = (() => {
        const keepForValue = new Map();
        const acc = [];
        for (const { rule, index } of group) {
          if (!normalized.includes(rule.value)) {
            acc.push(index);
          } else {
            if (keepForValue.has(rule.value)) acc.push(index);
            else keepForValue.set(rule.value, index);
          }
        }
        return acc.sort((a, b) => b - a);
      })();

      // Batch removal — one re-render instead of N
      if (toRemove.length) remove(toRemove);

      const remaining = group
        .filter(({ index }) => !toRemove.includes(index))
        .sort((a, b) => normalized.indexOf(a.rule.value) - normalized.indexOf(b.rule.value))
        .map(({ index }) => index);

      // After remove(), indices shift; convert pre-removal indices to post-removal.
      const getNewIndex = oldIdx => oldIdx - toRemove.filter(r => r < oldIdx).length;

      remaining.forEach((oldIdx, i) => {
        if (i < normalized.length)
          update(getNewIndex(oldIdx), { ...template, value: normalized[i], operator });
      });

      const insertCount = normalized.length - remaining.length;
      if (insertCount > 0) {
        const insertAfter = remaining.length
          ? getNewIndex(Math.max(...remaining))
          : (flagRulesWithIndex[0]?.index ?? -1);
        for (let i = 0; i < insertCount; i++) {
          insert(insertAfter + 1 + i, {
            ...template,
            value: normalized[remaining.length + i],
            operator,
          });
        }
      }
    },
    [fieldOrder, defaultOperator, flagRulesWithIndex, remove, update, insert],
  );

  // Sync multi-select block values: one rule per selected value
  const syncBlockValues = useCallback(
    newValues => {
      const rules = allRulesRef.current;
      const order = fieldOrder;
      const group = rules
        .map((r, i) => ({ rule: r, index: i }))
        .filter(({ rule }) => rule.field === order && rule.rule_type === 'block');
      const normalized = Array.isArray(newValues) && newValues.length ? newValues : [''];
      const operator = group[0]?.rule?.operator ?? defaultOperator;
      const template = group[0]?.rule
        ? { ...group[0].rule }
        : {
            rule_type: 'block',
            field: order,
            operator,
            value:
              field.type === FieldTypes.OpinionScale || field.type === FieldTypes.StarRating
                ? DEFAULT_BLOCK_VALUE
                : '',
            severity: '',
            message: group[0]?.rule?.message ?? '',
            active: true,
          };

      const toRemove = (() => {
        const keepForValue = new Map();
        const acc = [];
        for (const { rule, index } of group) {
          if (!normalized.includes(rule.value)) {
            acc.push(index);
          } else {
            if (keepForValue.has(rule.value)) acc.push(index);
            else keepForValue.set(rule.value, index);
          }
        }
        return acc.sort((a, b) => b - a);
      })();

      // Batch removal — one re-render instead of N
      if (toRemove.length) remove(toRemove);

      const remaining = group
        .filter(({ index }) => !toRemove.includes(index))
        .sort((a, b) => normalized.indexOf(a.rule.value) - normalized.indexOf(b.rule.value))
        .map(({ index }) => index);

      // After remove(), indices shift; convert pre-removal indices to post-removal.
      const getNewIndex = oldIdx => oldIdx - toRemove.filter(r => r < oldIdx).length;

      remaining.forEach((oldIdx, i) => {
        if (i < normalized.length)
          update(getNewIndex(oldIdx), { ...template, value: normalized[i], operator });
      });

      const insertCount = normalized.length - remaining.length;
      if (insertCount > 0) {
        const insertAfter = remaining.length
          ? getNewIndex(Math.max(...remaining))
          : (blockRulesWithIndex[0]?.index ?? -1);
        for (let i = 0; i < insertCount; i++) {
          insert(insertAfter + 1 + i, {
            ...template,
            value: normalized[remaining.length + i],
            operator,
          });
        }
      }
    },
    [fieldOrder, defaultOperator, field.type, blockRulesWithIndex, remove, update, insert],
  );

  useEffect(() => {
    setMessage('');
  }, [rulesCount]);

  // Master flagging toggle
  const handleFlaggingMasterToggle = useCallback(() => {
    if (flaggingOn) {
      flagRulesWithIndex.forEach(({ rule, index }) => {
        update(index, { ...rule, active: false });
      });
    } else {
      if (flagRulesWithIndex.length > 0) {
        flagRulesWithIndex.forEach(({ rule, index }) => {
          update(index, { ...rule, active: true });
        });
      } else {
        SEVERITIES.forEach(({ key }) => {
          append({
            rule_type: 'flag',
            field: fieldOrder,
            operator: defaultOperator,
            value: '',
            severity: key,
            message: '',
            active: true,
          });
        });
      }
    }
  }, [flaggingOn, flagRulesWithIndex, update, append, fieldOrder, defaultOperator]);

  // Block toggle
  const handleBlockingToggle = useCallback(() => {
    if (blockingOn) {
      if (blockRuleWithIndex) {
        const { rule, index } = blockRuleWithIndex;
        if (rule.value && rule.value !== '') {
          update(index, { ...rule, active: false });
        } else {
          remove(index);
        }
      }
    } else {
      if (blockRuleWithIndex) {
        update(blockRuleWithIndex.index, { ...blockRuleWithIndex.rule, active: true });
      } else {
        append({
          rule_type: 'block',
          field: fieldOrder,
          operator: defaultOperator,
          value: getDefaultBlockValue(),
          severity: '',
          message: '',
          active: true,
        });
      }
    }
  }, [
    blockingOn,
    blockRuleWithIndex,
    update,
    remove,
    append,
    fieldOrder,
    defaultOperator,
    getDefaultBlockValue,
  ]);

  return (
    <Accordion
      header={
        <span className="text-neutral-7 text-xl font-medium flex items-center gap-4">
          {rulesCount > 0 && (
            <span className="relative">
              <HugeiconsIcon icon={SlidersHorizontalIcon} size={20} className="text-neutral-6" />
              <span
                className={cn(
                  'absolute -top-1 left-[12px] text-xs rounded-full w-5 h-5 flex items-center justify-center bg-primary-4 text-white',
                )}>
                {rulesCount}
              </span>
            </span>
          )}{' '}
          Rules Configurations
        </span>
      }
      defaultCollapsed={true}>
      {/* Hidden inputs for ALL rules (active or not) so inactive rules are submitted */}
      {flagRulesWithIndex.map(({ rule, index: idx }) => (
        <Fragment key={`flag-hidden-${idx}`}>
          <input type="hidden" {...register(`rules.${idx}.rule_type`)} defaultValue="flag" />
          <input type="hidden" {...register(`rules.${idx}.field`)} defaultValue={fieldOrder} />
          <input
            type="hidden"
            {...register(`rules.${idx}.severity`)}
            defaultValue={rule.severity}
          />
          <input type="hidden" {...register(`rules.${idx}.active`)} />
        </Fragment>
      ))}
      {blockRulesWithIndex.map(({ rule, index: idx }) => (
        <Fragment key={`block-hidden-${idx}`}>
          <input type="hidden" {...register(`rules.${idx}.rule_type`)} defaultValue="block" />
          <input type="hidden" {...register(`rules.${idx}.field`)} defaultValue={fieldOrder} />
          <input type="hidden" {...register(`rules.${idx}.severity`)} defaultValue="" />
          <input type="hidden" {...register(`rules.${idx}.active`)} />
        </Fragment>
      ))}

      <div className="flex flex-col border border-neutral-4 rounded-2xl mt-2 min-w-0">
        {/* ── Flagging Rules ── */}
        <div className="px-4 py-4 border-b border-neutral-4 min-w-0">
          <div className="flex items-start justify-between gap-4 min-w-0">
            <div className="flex flex-col gap-2">
              <span className="text-neutral-6 text-xl font-medium truncate min-w-0">
                Flagging Rules
              </span>
              <span className="text-text-tertiary text-lg font-regular">
                Automatically flag responses in your reports when an answer meets the conditions
                below. If multiple rules match, the highest severity level will be shown.
              </span>
            </div>
            <ToggleSwitch
              name="flaggingRules"
              register={() => ({})}
              usePrimaryColor={true}
              inputProps={{
                checked: flaggingOn,
                onChange: handleFlaggingMasterToggle,
              }}
              noMargin
              className="!p-0 !border-0 !bg-transparent !h-auto !min-h-0 !w-auto !mb-0"
            />
          </div>

          {message && message !== '' && (
            <FormMessage message={message} type="Info" noMargin closeable={false} />
          )}

          {flaggingOn && (
            <div className="flex flex-col gap-4 min-w-0">
              {SEVERITIES.map(({ key, label, icon, iconClass, textClass }) => {
                const rulesForSeverity = flagRulesWithIndex.filter(
                  ({ rule }) => rule.severity === key,
                );
                if (!rulesForSeverity.length) return null;

                return (
                  <div
                    key={key}
                    className="flex-1 min-w-0 w-full sm:w-auto flex items-stretch gap-2">
                    <FlagConditionRow
                      rulesForSeverity={rulesForSeverity}
                      register={register}
                      field={field}
                      icon={icon}
                      label={label}
                      iconClass={iconClass}
                      textClass={textClass}
                      syncFlagValues={isMultiValueField(field?.type) ? syncFlagValues : undefined}
                      defaultOperator={defaultOperator}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Blocking Rules ── */}
        <div className="px-4 py-4 min-w-0">
          <div className="flex items-start justify-between gap-4 min-w-0">
            <div className="flex flex-col gap-2">
              <span className="text-neutral-6 text-xl font-medium truncate min-w-0">
                Blocking Rules
              </span>
              <span className="text-text-tertiary text-lg font-regular">
                Prevent a candidate or referee from proceeding with the form if their answer meets
                the condition below. The message configured will be displayed to them when blocked.
              </span>
            </div>
            <ToggleSwitch
              name="blockingRules"
              register={() => ({})}
              inputProps={{
                checked: blockingOn,
                onChange: handleBlockingToggle,
              }}
              noMargin
              usePrimaryColor
              className="!p-0 !border-0 !bg-transparent !h-auto !min-h-0 !w-auto !mb-0"
            />
          </div>

          {blockingOn && blockRulesWithIndex.length > 0 && (
            <BlockRuleEditor
              blockIndex={blockRulesWithIndex[0].index}
              register={register}
              field={field}
              allRules={allRules}
              fieldOrder={fieldOrder}
              blockRulesForField={blockRulesWithIndex}
              syncBlockValues={syncBlockValues}
              defaultOperator={defaultOperator}
            />
          )}
        </div>
      </div>
    </Accordion>
  );
};
